import { getCheckpointBenchmark } from '../data/checkpointBenchmarks';
import {
  isOverDisplayMax,
  markerPercent,
  nextTierGoal,
  resolveLearningTier,
} from '../lib/learningBenchmark';

interface LearningBenchmarkBarProps {
  checkpointId: string;
  words: number;
  variant: 'full' | 'compact';
}

const ZONE_BG = [
  'bg-amber-100/80 dark:bg-amber-500/15',
  'bg-amber-200/90 dark:bg-amber-500/25',
  'bg-indigo-100/90 dark:bg-indigo-500/20',
  'bg-indigo-200 dark:bg-indigo-500/30',
];

export default function LearningBenchmarkBar({
  checkpointId,
  words,
  variant,
}: LearningBenchmarkBarProps) {
  const benchmark = getCheckpointBenchmark(checkpointId);
  if (!benchmark) return null;

  const { tier, index } = resolveLearningTier(benchmark, words);
  const pct = markerPercent(benchmark, words);
  const goal = nextTierGoal(benchmark, words);
  const overMax = isOverDisplayMax(benchmark, words);

  if (variant === 'compact') {
    return (
      <div className="mt-2 space-y-1">
        <div className="relative h-2 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
          <div className="flex h-full w-full">
            {benchmark.tiers.map((_, i) => (
              <div
                key={i}
                className={`h-full flex-1 ${i === index ? ZONE_BG[Math.min(i, ZONE_BG.length - 1)] : 'bg-transparent'}`}
              />
            ))}
          </div>
          <div
            className="absolute top-0 h-full w-0.5 bg-amber-700 dark:bg-amber-300"
            style={{ left: `${pct}%` }}
          />
        </div>
        <p className="text-xs text-muted">
          {tier.shortLabel ?? tier.label} · {words} ord
        </p>
      </div>
    );
  }

  return (
    <section className="surface p-5">
      <p className="label">Dit niveau lige nu</p>
      <p className="mt-2 text-xl font-semibold text-strong">{tier.label}</p>
      <p className="mt-1 text-sm text-muted">{benchmark.contextLine}</p>

      <div className="relative mt-4 h-10 overflow-hidden rounded-xl ring-1 ring-zinc-200 dark:ring-white/10">
        <div className="flex h-full w-full">
          {benchmark.tiers.map((t, i) => (
            <div
              key={t.minWords}
              className={`flex flex-1 items-end justify-center pb-1 text-[10px] font-medium ${
                i === index
                  ? `${ZONE_BG[Math.min(i, ZONE_BG.length - 1)]} text-amber-950 dark:text-amber-100`
                  : 'bg-zinc-50 text-faint dark:bg-zinc-900/40'
              }`}
            >
              {t.shortLabel ?? ''}
            </div>
          ))}
        </div>
        <div
          className="absolute top-1 bottom-1 w-1 rounded-full bg-amber-600 shadow dark:bg-amber-300"
          style={{ left: `calc(${pct}% - 2px)` }}
        />
      </div>

      {overMax && (
        <p className="mt-2 text-sm font-medium text-indigo-700 dark:text-indigo-300">
          Over forventet for dette niveau — flot arbejde!
        </p>
      )}
      {goal && <p className="mt-2 text-xs text-faint">{goal}</p>}
    </section>
  );
}
